import React, { useState } from 'react';
import { ChevronLeft, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../../services/apiService';
// Import your permissions JSON
import permissionsConfig from '../../utils/permission.json';

const CreateAccountForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    userId: '',
    email: '',
    companyInfo: '',
    userType: '', // This will store the backend enum value
    permissions: {},
  });

  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [success, setSuccess] = useState('');

  // **User type mapping - Display name to backend enum**
  const userTypeMapping = {
    "Power Admin": "powerAdmin",
    "Admin": "admin", 
    "OBM Expert": "obmExpert",
    "OBM RD": "obmRD",
    "OBM QM": "obmQM",
    "GPM": "gpm",
    "ODM": "odm"
  };

  // **Reverse mapping for display purposes**
  const displayNameMapping = {
    "powerAdmin": "Power Admin",
    "admin": "Admin",
    "obmExpert": "OBM Expert",
    "obmRD": "OBM RD",
    "obmQM": "OBM QM",
    "gpm": "GPM",
    "odm": "ODM"
  };

  // **Permission display names mapping**
  const content = {
    "LL Mura Spot Line Band Issue Labeling": "LL Mura Spot Line Band Issue Labeling",   
    "Mura Spot Line Band Issue Detection": "Mura Spot Line Band Issue Detection",
    "Audio Issue Visualization": "Audio Issue Visualization",
    "Statistics and Analysis": "Statistics and Analysis",
    "HW Change Verification Assessment": "HW Change Verification Assessment",
    "Management": "Management",
    "Template Management": "Template Management",
    "Photo Management": "Photo Management"
  };
  
  const PermissionMapper = {
    LLMuraSpotLineBandIssueLabeling: content["LL Mura Spot Line Band Issue Labeling"],
    MuraSpotLineBandIssueDetection: content["Mura Spot Line Band Issue Detection"],
    AudioIssueVisualization: content["Audio Issue Visualization"],
    StatisticsAndAnalysis: content["Statistics and Analysis"],
    HWChangeVerificationAssessment: content["HW Change Verification Assessment"],
    Management: content["Management"],
    templateManagement: content["Template Management"],
    photoManagement: content["Photo Management"]
  };

  // **Get permissions based on user type (using backend enum value)**
  const getPermissionsForUserType = (userTypeEnum) => {
    if (!userTypeEnum) return {};

    const permissions = {};
    Object.keys(permissionsConfig).forEach(permission => {
      // Use the backend enum value directly
      if (permissionsConfig[permission].hasOwnProperty(userTypeEnum)) {
        permissions[permission] = permissionsConfig[permission][userTypeEnum] || false;
      }
    });
    return permissions;
  };

  // **Get available permissions for current user type**
  const getAvailablePermissions = () => {
    if (!formData.userType) return [];

    return Object.keys(permissionsConfig).filter(permission => 
      permissionsConfig[permission].hasOwnProperty(formData.userType)
    );
  };
  const allowedDomains = [
  "@tpv-tech.com",
  "@ktc.com",
  "@ktc.cn",
  "@skyworth.com",
  "@morshif.net",
  "@cultraview.com",
  "@cvte.com",
  "@lntdisplayfj.com",
  "@ctx-china.com",
  "@szmtc.com.cn",
  "@hengfakj.com",
  "@ostar-display.com",
  "@hannovo.com",
  "@lzopoto.com",
  "@cn-tianji.com",
  "@deweco.com.cn",
  "@ostar-wh.cn",
  "@ostar-display.cn",
  "@boe.com.cn"
  
];

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "email") {
      const lowerCaseEmail = value.toLowerCase();

       if (!allowedDomains.some(domain => lowerCaseEmail.endsWith(domain))) {
      setError("Unsupported email domain policy");
      setSuccess("");
    } else {
      setSuccess("Email is valid"); 
      setError("");
    }

      setFormData({ ...formData, [name]: lowerCaseEmail });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const togglePermission = (key) => {
    setFormData((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
    }));
  };

  const toggleAllPermissions = () => {
    const availablePermissions = getAvailablePermissions();
    const allSelected = availablePermissions.every(key => formData.permissions[key]);
    
    const updatedPermissions = { ...formData.permissions };
    availablePermissions.forEach(key => {
      updatedPermissions[key] = !allSelected;
    });

    setFormData((prev) => ({
      ...prev,
      permissions: updatedPermissions,
    }));
  };

  const selectUserType = (displayName) => {
    // **Convert display name to backend enum value**
    const backendEnumValue = userTypeMapping[displayName];
    const newPermissions = getPermissionsForUserType(backendEnumValue);
    
    setFormData({ 
      ...formData, 
      userType: backendEnumValue, // Store the backend enum value
      permissions: newPermissions
    });
    setShowDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // **The formData.userType is already the correct backend enum value**
      console.log("Sending payload:", formData); // This will now show "powerAdmin" etc.
      
      const response = await registerUser(formData);

      if (response && response.defaultPassword) {
        setSuccessMessage(`User created successfully! Default password: ${response.defaultPassword}`);
        navigate('/account-management');
      } else {
        throw new Error('Registration failed. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = allowedDomains.some(domain => formData.email.endsWith(domain)) && formData.userType !== '';
  const availablePermissions = getAvailablePermissions();
  const allPermissionsSelected = availablePermissions.length > 0 && 
    availablePermissions.every(key => formData.permissions[key]);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4">
      <div className="flex items-center mb-6">
        <button className="p-2 -ml-2" onClick={() => navigate("/account-management")}>
          <ChevronLeft className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold ml-2">Create Account</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          name="userId"
          placeholder="User Name"
          value={formData.userId}
          onChange={handleChange}
          required
          className="w-full bg-gray-800 text-gray-300 px-4 py-3 rounded-lg"
        />

        <input
          type="email"
          name="email"
          placeholder="Email Address"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full bg-gray-800 text-gray-300 px-4 py-3 rounded-lg"
        />
        
         {error && <p className="text-red-500 text-sm">{error}</p>} 
        {success && <p className="text-green-500 text-sm mt-1">{success}</p>} 


        <input
          type="text"
          name="companyInfo"
          placeholder="Company Info"
          value={formData.companyInfo}
          onChange={handleChange}
          maxLength="16"
          required
          className="w-full bg-gray-800 text-gray-300 px-4 py-3 rounded-lg"
        />

        <div className="relative">
          <button
            type="button"
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full bg-gray-800 text-gray-300 px-4 py-3 rounded-lg flex justify-between items-center"
          >
            {/* **Display the user-friendly name but store the backend enum** */}
            <span>{formData.userType ? displayNameMapping[formData.userType] : 'Select User Type'}</span>
            <ChevronDown className="h-5 w-5" />
          </button>
          {showDropdown && (
            <div className="absolute left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg overflow-hidden z-10">
              {Object.keys(userTypeMapping).map((displayName) => (
                <button
                  key={displayName}
                  type="button"
                  onClick={() => selectUserType(displayName)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-700"
                >
                  {displayName}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* **Permissions Section** */}
        {formData.userType && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Permissions</h2>
              {availablePermissions.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllPermissions}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                    ${allPermissionsSelected ? 'bg-blue-600' : 'bg-gray-700'}`}
                >
                  <span className="sr-only">Toggle all permissions</span>
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${allPermissionsSelected ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              )}
            </div>
            
            {availablePermissions.length > 0 ? (
              <div className="space-y-4">
                {availablePermissions.map((key) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <span className="text-gray-300">
                      {PermissionMapper[key] || key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())}
                    </span>
                    <button
                      type="button"
                      onClick={() => togglePermission(key)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                        ${formData.permissions[key] ? 'bg-blue-600' : 'bg-gray-700'}`}
                    >
                      <span className="sr-only">Toggle {key}</span>
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                          ${formData.permissions[key] ? 'translate-x-6' : 'translate-x-1'}`}
                      />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">No permissions available for this user type</div>
            )}
          </div>
        )}

        {successMessage && <p className="text-green-500 text-center">{successMessage}</p>}

        <button
          type="submit"
          disabled={!isFormValid || loading}
          className={`w-full py-3 rounded-lg mt-6 transition-colors ${
            !isFormValid || loading ? 'bg-gray-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? 'Creating...' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default CreateAccountForm;
